import React, { useState, useEffect } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { IoInformationCircle } from "react-icons/io5";
import { TbListDetails } from "react-icons/tb";
import Select from 'react-select';

const EditUserAnnouncements = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState({
    title: '',
    message: '',
    users: [], // multiple users
    files: [],
  });
  const [allUsers, setAllUsers] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await fetch('http://localhost:5544/api/user/getAllUsers');
        if (!response.ok) throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
        const data = await response.json();

        // ✅ filter only active users
        const activeUsers = Array.isArray(data) ? data.filter(user => user.status === "active") : [];
        setAllUsers(activeUsers);
      } catch (error) {
        setError(error.message || 'Error fetching users.');
      }
    };

    const fetchAnnouncement = async () => {
      try {
        const response = await fetch(`http://localhost:5544/api/user/announcements/${id}`);
        if (!response.ok) throw new Error(`Failed to fetch announcement: ${response.status} ${response.statusText}`);
        const data = await response.json();
        setAnnouncement({
          title: data.title || '',
          message: data.message || '',
          users: Array.isArray(data.users) ? data.users : [],
          files: Array.isArray(data.files) ? data.files : [],
        });
      } catch (error) {
        setError(error.message || 'Error fetching announcement.');
      }
    };

    if (id) {
      fetchAllUsers();
      fetchAnnouncement();
    } else {
      setError('Invalid announcement ID');
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAnnouncement((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUsersChange = (selectedOptions) => {
    const selectedUsers = selectedOptions.map(opt => ({
      userId: opt.value,
      name: opt.label,
    }));
    setAnnouncement((prev) => ({
      ...prev,
      users: selectedUsers,
    }));
  };

  const handleFileChange = (e) => {
    setSelectedFiles(e.target.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setShowAlert(false);

    try {
      const formData = new FormData();
      formData.append('title', announcement.title);
      formData.append('message', announcement.message);
      formData.append('users', JSON.stringify(announcement.users));

      for (let i = 0; i < selectedFiles.length; i++) {
        formData.append('files', selectedFiles[i]);
      }

      const response = await fetch(`http://localhost:5544/api/user/announcements/${id}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to update announcement: ${response.status} ${response.statusText}`);
      }

      setShowAlert(true);
      setTimeout(() => navigate('/user-announcements'), 2000);
    } catch (error) {
      setError(error.message || 'Error updating announcement.');
    }
  };

  // Helper to construct full file URL
  const getFileUrl = (fileUrl) => {
    if (fileUrl.startsWith('http')) {
      return fileUrl;
    }
    return `http://localhost:5544${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
  };

  return (
    <div className="container-fluid">
      <div className="edit-announcement-container">
        <h1 style={{ marginTop: '60px', fontSize: '2rem', color: '#0353a4' }}>
          <IoInformationCircle className="salary-icon" /> Announcements Information
        </h1>
        <hr />
        <h2 className="salary-subtitle">
          <TbListDetails className="salary-icon" /> Announcements Details
        </h2>
        <hr />
        <div className="table-container">
          {showAlert && (
            <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
              🎉 Announcement updated successfully!
            </Alert>
          )}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="title" className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={announcement.title}
                onChange={handleChange}
                required
                placeholder="Enter announcement title"
              />
            </Form.Group>
            <Form.Group controlId="message" className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                name="message"
                value={announcement.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder="Enter announcement message"
              />
            </Form.Group>

            {/* react-select multi dropdown */}
            <Form.Group controlId="users" className="mb-3">
              <Form.Label>Select Users <span style={{ color: 'red' }}>*</span></Form.Label>
              <Select
                isMulti
                options={allUsers.map(user => ({
                  value: user.userId,
                  label: user.name,
                }))}
                value={announcement.users.map(u => ({
                  value: u.userId,
                  label: u.name,
                }))}
                onChange={handleUsersChange}
                placeholder="Select users..."
              />
            </Form.Group>

            <Form.Group controlId="files" className="mb-3">
              <Form.Label>Files</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/jpg,application/pdf"
              />
              {announcement.files.length > 0 && (
                <div className="mt-2">
                  <p>Existing files:</p>
                  <ul>
                    {announcement.files.map((file, index) => (
                      <li key={index}>
                        <a href={getFileUrl(file.fileUrl)} target="_blank" rel="noopener noreferrer">
                          {file.fileName || `File ${index + 1}`}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Form.Group>

            <div className="py-3">
              <Button type="submit" variant="primary">
                Update Announcement
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EditUserAnnouncements;